import React, { useState } from 'react';
import { CheckCircle, Circle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const ReadinessAssessment = () => {
  const [answers, setAnswers] = useState({});
  const { t } = useLanguage();

  const questions = [
    {
      id: 1,
      key: 'vision',
      optionKeys: ['veryClear', 'partlyClear', 'unclear', 'noVision']
    },
    {
      id: 2,
      key: 'resistance',
      optionKeys: ['veryLow', 'moderate', 'high', 'veryHigh']
    },
    {
      id: 3,
      key: 'culture',
      optionKeys: ['none', 'few', 'some', 'many']
    },
    {
      id: 4,
      key: 'salesAgility',
      optionKeys: ['veryAgile', 'partlyAgile', 'traditional', 'rigid']
    }
  ];

  const handleAnswer = (questionId, answerId) => {
    setAnswers(prev => ({ ...prev, [questionId]: answerId }));
  };

  // Score Map arbeitet mit stabilen IDs, nicht mit Texten
  const scoreMap = {
    'vision.veryClear': 4,
    'vision.partlyClear': 3,
    'vision.unclear': 2,
    'vision.noVision': 1,

    'resistance.veryLow': 4,
    'resistance.moderate': 3,
    'resistance.high': 2,
    'resistance.veryHigh': 1,

    'culture.none': 4,
    'culture.few': 3,
    'culture.some': 2,
    'culture.many': 1,

    'salesAgility.veryAgile': 4,
    'salesAgility.partlyAgile': 3,
    'salesAgility.traditional': 2,
    'salesAgility.rigid': 1
  };

  const calculateScore = () => {
    const totalScore = Object.values(answers).reduce(
      (sum, answerId) => sum + (scoreMap[answerId] || 0),
      0
    );
    const maxScore = questions.length * 4;
    return Math.round((totalScore / maxScore) * 100);
  };

  const score =
    Object.keys(answers).length === questions.length ? calculateScore() : null;

  const getRecommendation = (score, t) => {
    if (score >= 75)
      return {
        level: t('readiness.levelHigh'),
        color: 'text-green-600',
        bg: 'bg-green-50',
        message: t('readiness.messageHigh'),
        icon: CheckCircle
      };
    if (score >= 50)
      return {
        level: t('readiness.levelMedium'),
        color: 'text-orange-600',
        bg: 'bg-orange-50',
        message: t('readiness.messageMedium'),
        icon: AlertCircle
      };
    return {
      level: t('readiness.levelLow'),
      color: 'text-red-600',
      bg: 'bg-red-50',
      message: t('readiness.messageLow'),
      icon: AlertCircle
    };
  };

  const recommendation = score !== null ? getRecommendation(score, t) : null;

  return (
    <div className="bg-white rounded-2xl p-8 border border-slate-200 shadow-lg">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-semibold text-slate-900 mb-2">
          {t('readiness.title')}
        </h3>
        <p className="text-slate-600">
          {t('readiness.subtitle')}
        </p>
      </div>

      <div className="space-y-6 mb-8">
        {questions.map(q => {
          const questionKey = q.key;
          const questionText = t(
            `readiness.questions.${questionKey}.question`
          );

          return (
            <div key={q.id} className="space-y-3">
              <p className="font-medium text-slate-900">{questionText}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {q.optionKeys.map(optionKey => {
                  const answerId = `${questionKey}.${optionKey}`;
                  const label = t(
                    `readiness.questions.${questionKey}.options.${optionKey}`
                  );
                  const isActive = answers[q.id] === answerId;

                  return (
                    <button
                      key={optionKey}
                      onClick={() => handleAnswer(q.id, answerId)}
                      className={`p-3 rounded-lg border-2 transition-all duration-200 text-sm ${
                        isActive
                          ? 'border-purple-600 bg-purple-50 text-purple-900 font-medium'
                          : 'border-slate-200 hover:border-purple-300 text-slate-700'
                      }`}
                    >
                      {isActive ? (
                        <CheckCircle className="w-4 h-4 inline mr-1" />
                      ) : (
                        <Circle className="w-4 h-4 inline mr-1" />
                      )}
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {recommendation && (
        <div
          className={`${recommendation.bg} rounded-xl p-6 border-2 border-${recommendation.color.replace(
            'text-',
            ''
          )}`}
        >
          <div className="flex items-start gap-4">
            <recommendation.icon
              className={`w-8 h-8 ${recommendation.color} flex-shrink-0`}
            />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h4
                  className={`text-lg font-semibold ${recommendation.color}`}
                >
                  {t('readiness.scoreLabel')}: {score}%
                </h4>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${recommendation.color} ${recommendation.bg}`}
                >
                  {recommendation.level}
                </span>
              </div>
              <p className="text-slate-700 leading-relaxed">
                {recommendation.message}
              </p>
              <div className="mt-4">
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${
                      score >= 75
                        ? 'bg-green-600'
                        : score >= 50
                        ? 'bg-orange-600'
                        : 'bg-red-600'
                    }`}
                    style={{ width: `${score}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
